import { AxiosInstance } from 'axios';
import { BillingPlans, BillingRedirect, BillingSummary, PlanCode } from '../types';

/**
 * Billing API endpoints (`api/billing`). Platform-agnostic.
 */
export class BillingApi {
  constructor(private readonly client: AxiosInstance) {}

  /**
   * The user's plan, limits, usage and trials.
   * @param refresh Ask Login even when the stored plan is fresh (after returning from Stripe).
   */
  async getSummary(refresh = false): Promise<BillingSummary> {
    const response = await this.client.get<BillingSummary>('/api/billing/me', {
      params: refresh ? { refresh: true } : undefined,
    });
    return response.data;
  }

  /** The plans that can be bought, with prices when Stripe could be read. */
  async getPlans(): Promise<BillingPlans> {
    const response = await this.client.get<BillingPlans>('/api/billing/plans');
    return response.data;
  }

  /** Starts Stripe Checkout, or returns the Customer Portal when the user already subscribes. */
  async startCheckout(planCode: PlanCode): Promise<BillingRedirect> {
    const response = await this.client.post<BillingRedirect>('/api/billing/checkout', { planCode });
    return response.data;
  }

  /** Records a finished Checkout straight away (called on return from Stripe) and returns the new summary. */
  async syncCheckout(sessionId: string): Promise<BillingSummary> {
    const response = await this.client.post<BillingSummary>('/api/billing/checkout/sync', { sessionId });
    return response.data;
  }

  /** Opens the Stripe Customer Portal. */
  async openPortal(): Promise<BillingRedirect> {
    const response = await this.client.post<BillingRedirect>('/api/billing/portal');
    return response.data;
  }
}
